import { animationTransition } from '@shared/joykit/packages/core/utils';
import type { StyleSystemProps } from '@withjoy/joykit';
import { styled, Box } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';

export type ColorVariant = 'light' | 'dark';
export type StorefrontNavigatorSpacing = 'default' | 'compact';

export const StyledNavList = styled(Box)``;
export const StyledListItem = styled(Box)``;
export const StyledLink = styled(Box)``;
export const StyledBadge = styled(Box)``;

const navListSpacing: Record<StorefrontNavigatorSpacing, StyleSystemProps> = {
  default: {
    paddingX: 6,
    columnGap: [2, null, 6],
  },
  compact: {
    paddingX: 4,
    columnGap: 6,
    fontWeight: 600,
    fontSize: pxToRem(13),
    lineHeight: pxToRem(13),
  },
};

export const navListStyles = (spacing: StorefrontNavigatorSpacing): StyleSystemProps => ({
  ...navListSpacing[spacing],
  alignItems: 'center',
  height: '100%',
  display: 'inline-grid',
  gridAutoFlow: 'column',
});

export const listItemStyles: StyleSystemProps = {
  height: '100%',
  display: 'inline-flex',
  alignItems: 'center',
};

const COLOR_PROPS: Record<
  ColorVariant,
  {
    text: StyleSystemProps['color'];
    textPressed: StyleSystemProps['color'];
    badgeText: StyleSystemProps['color'];
    badgeBackgroundColor: StyleSystemProps['color'];
  }
> = {
  light: {
    text: 'white',
    textPressed: 'mono4',
    badgeBackgroundColor: 'white',
    badgeText: 'black',
  },
  dark: {
    text: 'black',
    textPressed: 'mono12',
    badgeBackgroundColor: 'mono14',
    badgeText: 'white',
  },
};

const LINK_SPACING: Record<StorefrontNavigatorSpacing, StyleSystemProps> = {
  default: {
    bottom: 4,
    borderBottom: '2px solid',
    outline: 'initial',
  },
  compact: {
    bottom: pxToRem(10),
    borderBottom: '1px solid',
    outline: 'none',
  },
};

export const linkStyles = (colorVariant: ColorVariant, spacing: StorefrontNavigatorSpacing): StyleSystemProps => {
  const colorValues = COLOR_PROPS[colorVariant];
  const spacingValues = LINK_SPACING[spacing];

  return {
    paddingX: 2,
    paddingY: 1,
    position: 'relative',
    color: colorValues.text,
    display: 'inline-flex',
    alignItems: 'center',
    height: '100%',
    transition: animationTransition('color'),

    _after: {
      content: '" "',
      position: 'absolute',
      bottom: spacingValues.bottom,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 'calc(100% - 10px)',
    },

    _hover: {
      _after: {
        borderBottom: '1px solid',
        borderBottomColor: colorValues.text,
      },
    },
    _active: {
      color: colorValues.textPressed,
    },

    _activeLink: {
      _after: {
        borderBottom: spacingValues.borderBottom,
        borderBottomColor: colorValues.text,
      },
    },

    _focusVisible: { outline: spacingValues.outline },
  };
};

const newBadgeSpacing: Record<StorefrontNavigatorSpacing, StyleSystemProps> = {
  default: {
    borderRadius: 2,
    marginLeft: 3,
  },
  compact: {
    borderRadius: 7,
    fontWeight: 600,
    fontSize: pxToRem(10),
    lineHeight: pxToRem(11),
  },
};

export const newBadgeStyles = (colorVariant: ColorVariant, spacing: StorefrontNavigatorSpacing): StyleSystemProps => {
  const colorValues = COLOR_PROPS[colorVariant];
  return {
    typographyVariant: 'label1',
    ...newBadgeSpacing[spacing],
    display: 'inline-block',
    paddingX: 2,
    paddingY: 1,
    backgroundColor: colorValues.badgeBackgroundColor,
    color: colorValues.badgeText,
  };
};
